// CuttingOrdersPageB24.js - обновленная версия с интеграцией деталировки
import React, { useState, useEffect } from "react";
import { API_URL } from '../config';
import BitrixAPI from "../api/b24";
import LoadingButton from "../components/LoadingButton";
import { FilesContainer } from "../components/FileViewer";
import GlassVisualizator from "../components/GlassVisualizator";
import PrintButton from "../components/PrintButton";
import { useHeader } from "../contexts/HeaderContext";
import { X, AlertTriangle, CheckCircle2, Sparkles, ArrowRight, Loader2, Search } from "lucide-react";

const CuttingOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [problemOrders, setProblemOrders] = useState([]);
  const [problemStats, setProblemStats] = useState({ byStage: {} });
  const [loading, setLoading] = useState(true);
  const [problemLoading, setProblemLoading] = useState(false);
const [selectedStageFilter, setSelectedStageFilter] = useState('all'); // Фильтр по стадиям
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [glassTypes, setGlassTypes] = useState(new Set());
  const [selectedGlassType, setSelectedGlassType] = useState(null);
  const [error, setError] = useState(null);
  
  // Состояние для фильтра просмотра
  const [viewFilter, setViewFilter] = useState("orders"); // "orders" или "details"
  
  // Состояния для деталировки всех заказов (как в ZakalkaOrdersPageB24)
  const [ordersDetails, setOrdersDetails] = useState({}); // {orderId: {glassDetails: [], selectedDetails: Set(), quantities: Map()}}
  const [ordersComments, setOrdersComments] = useState({}); // {orderId: "comments"}
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // ID заказов, для которых сейчас загружаются детали

  // Состояния для отслеживания статуса сохранения деталей (для каждой детали отдельно)
  const [detailSavingStatus, setDetailSavingStatus] = useState({}); // {`${orderId}_${detailId}`: 'idle' | 'saving' | 'saved' | 'error'}

  // Состояние для выбранных экземпляров в визуализаторе
  const [selectedInstances, setSelectedInstances] = useState(new Set()); // Set instanceId

  // Таймеры для debounce автосохранения (для каждой детали отдельно)
  const saveTimersRef = React.useRef({}); // {`${orderId}_${detailId}`: timeoutId}
  
  // Состояния для поиска
  const [searchTerm, setSearchTerm] = useState(''); // Поисковый запрос

  const { setHeaderExtras } = useHeader();

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}

  // Компонент индикатора статуса сохранения (для конкретной детали)
  const SaveStatusIndicator = ({ orderId, detailId }) => {
    const detailKey = `${orderId}_${detailId}`;
    const status = detailSavingStatus[detailKey] || 'idle';

    if (status === 'idle') {
      return null;
    }

    if (status === 'saving') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
          <svg className="animate-spin -ml-1 mr-1.5 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Сохранение...
        </span>
      );
    }

    if (status === 'saved') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
          <svg className="mr-1.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
          Сохранено
        </span>
      );
    }

    if (status === 'error') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
          <svg className="mr-1.5 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          Ошибка
        </span>
      );
    }

    return null;
  };

  // Функция загрузки деталей для заказа
  const loadOrderDetails = async (orderId) => {
    try {
      console.log(`🔄 Начинаем загрузку деталей для заказа ${orderId}`);
      const token = localStorage.getItem('token');
      
      const params = { 
        stage: 'rezka'
      };
      
      const response = await fetch(`${API_URL}/api/glass-details-fixed/${orderId}?stage=${params.stage}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.glassDetails) {
        const savedSelected = data.savedState?.selected ? JSON.parse(data.savedState.selected) : [];
        const savedQuantities = data.savedState?.quantities ? JSON.parse(data.savedState.quantities) : {};
        
        setOrdersDetails(prev => ({
          ...prev,
          [orderId]: {
            glassDetails: data.glassDetails,
            selectedDetails: new Set(savedSelected),
            quantities: new Map(Object.entries(savedQuantities).map(([k, v]) => [k, parseInt(v) || 1]))
          }
        }));
        
        if (data.savedState?.comments) {
          setOrdersComments(prev => ({
            ...prev,
            [orderId]: data.savedState.comments
          }));
        }
      }
    } catch (error) {
      console.error(`❌ Ошибка загрузки деталей для заказа ${orderId}:`, error);
      setOrdersDetails(prev => ({
        ...prev,
        [orderId]: {
          glassDetails: [],
          selectedDetails: new Set(),
          quantities: new Map(),
          loadError: error.message
        }
      }));
    }
  };

  // Автосохранение состояния детали на сервере (для конкретной детали)
  const autoSaveDetailState = async (orderId, detailId) => {
    const detailKey = `${orderId}_${detailId}`;

    try {
      // Получаем ТЕКУЩЕЕ состояние заказа из state
      setOrdersDetails(currentOrders => {
        const orderData = currentOrders[orderId];
        if (!orderData) {
          console.log(`⚠️ Нет данных для заказа ${orderId}, пропускаем сохранение`);
          return currentOrders;
        }

        // Устанавливаем статус "сохраняется" для конкретной детали
        setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'saving' }));

        const selectedArray = Array.from(orderData.selectedDetails);
        const quantitiesObj = Object.fromEntries(orderData.quantities);
        const comments = ordersComments[orderId] || '';

        console.log(`💾 Отправляем на сервер для заказа ${orderId}, деталь ${detailId}:`, {
          selectedDetails: selectedArray,
          selectedQuantities: quantitiesObj,
          totalGlassDetails: orderData.glassDetails?.length || 0
        });

        const token = localStorage.getItem('token');

        // Выполняем запрос
        fetch(`${API_URL}/api/glass-details/${orderId}/save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            stage: 'rezka',
            selectedDetails: selectedArray,
            selectedQuantities: quantitiesObj,
            comments: comments,
            glassDetails: orderData.glassDetails || [],
            autoSave: true
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Ошибка сохранения');
          }
          return response.json();
        })
        .then(data => {
          // Устанавливаем статус "сохранено" для конкретной детали
          setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'saved' }));
          console.log(`✅ Деталь ${detailId} заказа ${orderId} сохранена на сервере`);

          // Через 2 секунды возвращаем статус в idle
          setTimeout(() => {
            setDetailSavingStatus(prev => {
              const newState = { ...prev };
              delete newState[detailKey];
              return newState;
            });
          }, 2000);
        })
        .catch(error => {
          console.error(`❌ Ошибка сохранения детали ${detailId} заказа ${orderId}:`, error);
          setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'error' }));

          // Через 3 секунды возвращаем статус в idle
          setTimeout(() => {
            setDetailSavingStatus(prev => {
              const newState = { ...prev };
              delete newState[detailKey];
              return newState;
            });
          }, 3000);
        });

        return currentOrders; // Возвращаем неизменное состояние
      });
    } catch (error) {
      console.error(`❌ Критическая ошибка автосохранения для детали ${detailId} заказа ${orderId}:`, error);
      setDetailSavingStatus(prev => ({ ...prev, [detailKey]: 'error' }));

      setTimeout(() => {
        setDetailSavingStatus(prev => {
          const newState = { ...prev };
          delete newState[detailKey];
          return newState;
        });
      }, 3000);
    }
  };

  // Обработка переключения деталей с автосохранением
  const handleDetailToggle = (orderId, detailId, detail) => {
    const detailKey = `${orderId}_${detailId}`;

    setOrdersDetails(prev => {
      const orderData = prev[orderId] || { glassDetails: [], selectedDetails: new Set(), quantities: new Map() };
      const newSelectedDetails = new Set(orderData.selectedDetails);
      const newQuantities = new Map(orderData.quantities);

      if (newSelectedDetails.has(detailId)) {
        newSelectedDetails.delete(detailId);
        newQuantities.delete(detailId);
      } else {
        newSelectedDetails.add(detailId);
        newQuantities.set(detailId, detail.quantity || 1);
      }

      const updatedOrderData = {
        ...orderData,
        selectedDetails: newSelectedDetails,
        quantities: newQuantities
      };

      // Отменяем предыдущий таймер автосохранения для этой конкретной детали
      if (saveTimersRef.current[detailKey]) {
        clearTimeout(saveTimersRef.current[detailKey]);
      }

      // Автосохранение с debounce для конкретной детали
      saveTimersRef.current[detailKey] = setTimeout(() => {
        console.log(`🔄 Автосохранение детали ${detailId} заказа ${orderId}`);
        autoSaveDetailState(orderId, detailId);
        delete saveTimersRef.current[detailKey];
      }, 300);

      return {
        ...prev,
        [orderId]: updatedOrderData
      };
    });
  };

  // Обработка изменения количества с автосохранением
  const handleQuantityChange = (orderId, detailId, newQuantity, maxQuantity) => {
    if (newQuantity < 1 || newQuantity > maxQuantity) return;

    const detailKey = `${orderId}_${detailId}`;

    setOrdersDetails(prev => {
      const orderData = prev[orderId] || { glassDetails: [], selectedDetails: new Set(), quantities: new Map() };
      const newQuantities = new Map(orderData.quantities);
      newQuantities.set(detailId, newQuantity);

      const updatedOrderData = {
        ...orderData,
        quantities: newQuantities
      };

      // Отменяем предыдущий таймер автосохранения для этой детали
      if (saveTimersRef.current[detailKey]) {
        clearTimeout(saveTimersRef.current[detailKey]);
      }

      // Автосохранение с debounce для конкретной детали
      saveTimersRef.current[detailKey] = setTimeout(() => {
        console.log(`🔄 Автосохранение изменения количества для детали ${detailId} заказа ${orderId}`);
        autoSaveDetailState(orderId, detailId);
        delete saveTimersRef.current[detailKey];
      }, 500);

      return {
        ...prev,
        [orderId]: updatedOrderData
      };
    });
  };
  
  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-100 rounded-xl overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-emerald-100' : 'bg-blue-100'
          }`}
          style={{ width: `${progress}%` }}
        />

        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-lg shadow-sm transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-emerald-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          ) : (
            <ArrowRight className="w-4 h-4 text-white" />
          )}
        </div>

        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-emerald-700' : 'text-gray-500'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  // Функция сохранения выбранных деталей
  const saveSelectedDetails = async (orderId) => {
    try {
      const orderData = ordersDetails[orderId];
      if (!orderData) {
        console.log(`Нет данных деталировки для заказа ${orderId}, пропускаем сохранение`);
        return true;
      }

      const selectedArray = Array.from(orderData.selectedDetails);
      const quantitiesObj = Object.fromEntries(orderData.quantities);
      const comments = ordersComments[orderId] || '';

      console.log(`💾 Сохраняем детали для заказа ${orderId}:`, {
        selected: selectedArray,
        quantities: quantitiesObj,
        comments
      });
      
      console.log(`🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА РЕЗКИ:`, {
        orderData: orderData,
        selectedDetailsSet: Array.from(orderData.selectedDetails),
        quantitiesMap: Object.fromEntries(orderData.quantities),
        glassDetailsCount: orderData.glassDetails?.length || 0
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/glass-details/${orderId}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stage: 'rezka',
          selectedDetails: selectedArray,
          selectedQuantities: quantitiesObj,
          comments: comments,
          glassDetails: orderData.glassDetails || []
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        console.log(`✅ Детали для заказа ${orderId} успешно сохранены`);
        return true;
      } else {
        throw new Error(result.message || 'Не удалось сохранить детали');
      }
    } catch (error) {
      console.error(`❌ Ошибка сохранения деталей для заказа ${orderId}:`, error);
      throw error;
    }
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      console.log(`🔄 Начинаем завершение заказа ${orderId}`);
      
      // Сначала сохраняем выбранные детали
      console.log(`💾 Шаг 1: Сохраняем выбранные детали для заказа ${orderId}`);
      await saveSelectedDetails(orderId);
      
      // Затем переводим заказ на следующую стадию
      console.log(`⏭️ Шаг 2: Переводим заказ ${orderId} на следующую стадию`);
      const result = await BitrixAPI.updateStagecutting(orderId);
      
      console.log('Результат обновления стадии:', result);
  
      if (result && result.success) {
        setOrders(prevOrders => 
          prevOrders.filter(order => order.id !== orderId)
        );
        console.log(`✅ Заказ ${orderId} успешно завершен: детали сохранены и заказ переведен на следующую стадию`);
      } else {
        throw new Error(result?.message || 'Не удалось обновить стадию');
      }
  
    } catch (error) {
      console.error('Ошибка при обновлении стадии:', error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка при обновлении заказа: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };


  // Функция для загрузки всех проблемных заказов со всех стадий
  const fetchProblemOrders = async () => {
    try {
      setProblemLoading(true);
      setError(null);
      
      console.log('Загружаем проблемные заказы со всех стадий...');
      const response = await BitrixAPI.getAllProblemOrders();
      
      console.log('Получены проблемные заказы:', response);
      setProblemOrders(response.orders || []);
      setProblemStats(response);
      
      // Загружаем деталировку для проблемных заказов
      if (response.orders && Array.isArray(response.orders)) {
        console.log('📋 Начинаем параллельную загрузку деталей для', response.orders.length, 'проблемных заказов...');
        const detailPromises = response.orders.map(async (order) => {
          try {
            await loadOrderDetails(order.id);
            return { orderId: order.id, success: true };
          } catch (detailError) {
            return { orderId: order.id, success: false, error: detailError.message };
          }
        });
        
        // Ждем завершения всех запросов
        const detailResults = await Promise.allSettled(detailPromises);
        const successCount = detailResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const errorCount = detailResults.length - successCount;
        
        console.log(`🎯 ИТОГ загрузки деталей для проблемных заказов: ${successCount} успешно, ${errorCount} с ошибками`);
      }
      
    } catch (err) {
      console.error("Ошибка загрузки проблемных заказов:", err);
      setError(`Ошибка загрузки проблемных заказов: ${err.message}`);
    } finally {
      setProblemLoading(false);
    }
  };

  // ПРИМЕЧАНИЕ: Функция handleMarkDetailAsCompleted удалена
  // Теперь все исправления выполняются только через модальное окно деталировки

  // Функция для получения отфильтрованных проблемных заказов
  const getFilteredProblemOrders = () => {
    if (selectedStageFilter === 'all') {
      return problemOrders;
    }
    return problemOrders.filter(order => order.stage === selectedStageFilter);
  };

  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchOrders = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      console.log('Загружаем заказы для резки...');
      const response = await BitrixAPI.getActiveOrderscutting();

      console.log('Получены заказы:', response);

      if (response && Array.isArray(response)) {
        // Определяем новые заказы (которых еще нет в текущем списке)
        const newOrders = response.filter(newOrder =>
          !orders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0 && !isInitialLoad) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов:`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }

        setOrders(response || []);

        const temperedTypes = new Set(response.map(order => order.temperedGlass).filter(Boolean));
        const rawTypes = new Set(response.map(order => order.rawGlass).filter(Boolean));
        setGlassTypes(new Set([...temperedTypes, ...rawTypes]));

        // Загружаем деталировку только для новых заказов или всех при первой загрузке
        const ordersToLoadDetails = isInitialLoad ? response : newOrders;

        if (ordersToLoadDetails.length > 0) {
          console.log('📋 Начинаем параллельную загрузку деталей для', ordersToLoadDetails.length, 'заказов...');
          const detailPromises = ordersToLoadDetails.map(async (order) => {
            try {
              await loadOrderDetails(order.id);
              return { orderId: order.id, success: true };
            } catch (detailError) {
              return { orderId: order.id, success: false, error: detailError.message };
            }
          });

          // Ждем завершения всех запросов
          const detailResults = await Promise.allSettled(detailPromises);
          const successCount = detailResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
          const errorCount = detailResults.length - successCount;

          console.log(`🎯 ИТОГ загрузки деталей: ${successCount} успешно, ${errorCount} с ошибками`);
        }
      }

    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Поиск по номеру, названию..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
            style={{ width: "220px" }}
          />
        </div>
        <span className="text-sm text-gray-500">
          Заказов: <strong className="text-gray-900 font-semibold">{getFilteredOrders().length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, orders, ordersDetails]);

  // Первоначальная загрузка заказов
  useEffect(() => {
    fetchOrders(true);
  }, []);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive) {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log('🔄 Проверка новых заказов...');
      fetchOrders(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, orders]); // orders в зависимостях для сравнения с новыми

  // Заказы, доступные локации текущего пользователя:
  // цех "Маметовой, 3а к5" видит только детали 4мм, цех "Мамбетова 325а" - всё, кроме 4мм
  const LOCATION_THICKNESS_FILTERS = {
    'Маметовой, 3а к5': (thickness) => thickness === '4мм',
    'Мамбетова 325а': (thickness) => thickness !== '4мм'
  };

  const getLocationOrders = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};

    const matchesThickness = LOCATION_THICKNESS_FILTERS[user.workshopLocation];
    if (!matchesThickness) {
      return orders;
    }

    return orders.filter(order => {
      const orderData = ordersDetails[order.id];
      return orderData?.glassDetails?.some(detail => matchesThickness(detail.thickness));
    });
  };

  // Функция фильтрации заказов по поисковому запросу
  const getFilteredOrders = () => {
    const locationOrders = getLocationOrders();

    if (!searchTerm.trim()) return locationOrders;

    return locationOrders.filter(order => {
      const orderNumber = (order.orderNumber || order.id).toString().toLowerCase();
      const orderTitle = (order.title || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      // Поиск по номеру заказа и названию
      if (orderNumber.includes(searchLower) || orderTitle.includes(searchLower)) {
        return true;
      }
      
      // Поиск по деталям в заказе
      const orderData = ordersDetails[order.id];
      if (orderData && orderData.glassDetails) {
        return orderData.glassDetails.some(detail => {
          const originalText = (detail.originalText || '').toLowerCase();
          const description = (detail.description || '').toLowerCase();
          const glassType = (detail.glassType || '').toLowerCase();
          
          return originalText.includes(searchLower) || 
                 description.includes(searchLower) || 
                 glassType.includes(searchLower);
        });
      }
      
      return false;
    });
  };

  // Функция для рендеринга деталей внутри заказа
  const renderGlassDetails = (orderId, orderNumber) => {
    const orderData = ordersDetails[orderId];
    
    // Если есть ошибка загрузки, показываем специальное сообщение
    if (orderData && orderData.loadError) {
      return (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-1.5 text-red-600 mb-2 text-sm">
            <AlertTriangle className="w-4 h-4" />
            Ошибка загрузки деталей: {orderData.loadError}
          </div>
          <div className="text-gray-400 text-sm">
            Заказ отображается в упрощенном режиме. Попробуйте обновить страницу.
          </div>
        </div>
      );
    }

    if (!orderData || !orderData.glassDetails.length) {
      return (
        <div className="text-center py-4 text-gray-400 text-sm">
          Нет доступных деталей для выбора
        </div>
      );
    }

    // Группируем детали по типу стекла
    const groupedDetails = {};
    orderData.glassDetails.forEach(detail => {
      const glassType = detail.glassType || 'Неизвестный тип';
      if (!groupedDetails[glassType]) {
        groupedDetails[glassType] = [];
      }
      groupedDetails[glassType].push(detail);
    });

    return (
      <div className="space-y-4">
        {Object.entries(groupedDetails).map(([glassType, details]) => (
          <div key={glassType} className="border border-gray-200 rounded-xl p-3">
            <h4 className="font-semibold text-sm text-gray-800 mb-3">{glassType}</h4>
            <div className="space-y-2">
              {details.map((detail) => {
                const isSelected = orderData.selectedDetails.has(detail.id);
                const selectedQuantity = orderData.quantities.get(detail.id) || detail.quantity || 1;
                const maxQuantity = detail.quantity || 1;

                return (
                  <div
                    key={detail.id}
                    className={`flex flex-wrap items-center gap-2 p-2.5 border rounded-lg transition-colors ${
                      isSelected ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleDetailToggle(orderId, detail.id, detail)}
                        className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500/30 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm flex flex-wrap items-center gap-1.5">
                          <span className="break-all text-gray-800">{detail.originalText}</span>
                          <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full whitespace-nowrap font-medium">
                            №{orderNumber}
                          </span>
                          <SaveStatusIndicator orderId={orderId} detailId={detail.id} />
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleQuantityChange(orderId, detail.id, selectedQuantity - 1, maxQuantity)}
                          disabled={selectedQuantity <= 1}
                          className="w-6 h-6 rounded-full bg-red-50 hover:bg-red-100 text-red-600 text-sm font-bold disabled:opacity-40 transition-colors"
                        >
                          −
                        </button>
                        <span className="min-w-[2rem] text-center text-sm font-semibold text-gray-800">
                          {selectedQuantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(orderId, detail.id, selectedQuantity + 1, maxQuantity)}
                          disabled={selectedQuantity >= maxQuantity}
                          className="w-6 h-6 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-sm font-bold disabled:opacity-40 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Функция для рендеринга только деталей без карточек заказов
  const renderDetailsOnly = (ordersList) => {
    const allDetails = [];
    
    // Собираем все детали из всех заказов
    ordersList.forEach(order => {
      const orderData = ordersDetails[order.id];
      if (orderData && orderData.glassDetails) {
        orderData.glassDetails.forEach(detail => {
          allDetails.push({
            ...detail,
            orderId: order.id,
            orderNumber: order.orderNumber || order.id,
            orderTitle: order.title,
            isSelected: orderData.selectedDetails.has(detail.id),
            selectedQuantity: orderData.quantities.get(detail.id) || detail.quantity || 1,
            maxQuantity: detail.quantity || 1
          });
        });
      }
    });

    // Группируем детали по типу стекла
    const groupedDetails = {};
    allDetails.forEach(detail => {
      const glassType = detail.glassType || 'Неизвестный тип';
      if (!groupedDetails[glassType]) {
        groupedDetails[glassType] = [];
      }
      groupedDetails[glassType].push(detail);
    });

    if (allDetails.length === 0) {
      return (
        <div className="text-center py-16 text-gray-400 text-sm bg-white border border-gray-200 rounded-2xl">
          Нет доступных деталей для отображения
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Object.entries(groupedDetails).map(([glassType, details]) => (
          <div key={glassType} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{glassType}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {details.map((detail) => (
                <div
                  key={`${detail.orderId}_${detail.id}`}
                  className={`p-3 border rounded-xl transition-colors ${
                    detail.isSelected ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={detail.isSelected}
                        onChange={() => handleDetailToggle(detail.orderId, detail.id, detail)}
                        className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500/30"
                      />
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        №{detail.orderNumber}
                      </span>
                    </div>
                    {detail.isSelected && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleQuantityChange(detail.orderId, detail.id, detail.selectedQuantity - 1, detail.maxQuantity)}
                          disabled={detail.selectedQuantity <= 1}
                          className="w-5 h-5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold disabled:opacity-40 transition-colors"
                        >
                          −
                        </button>
                        <span className="mx-1.5 min-w-[2rem] text-center text-sm font-semibold text-gray-800">
                          {detail.selectedQuantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(detail.orderId, detail.id, detail.selectedQuantity + 1, detail.maxQuantity)}
                          disabled={detail.selectedQuantity >= detail.maxQuantity}
                          className="w-5 h-5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-xs font-bold disabled:opacity-40 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {detail.originalText}
                  </div>
                  <SaveStatusIndicator orderId={detail.orderId} detailId={detail.id} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center gap-3 py-16 text-gray-500">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">Ойланып жатырмын...</span>
    </div>
  );

  const filteredOrders = getFilteredOrders();

  return (
    <div className="container mx-auto p-2 sm:p-4 overflow-x-hidden">
      
      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 rounded-xl mb-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <strong className="text-sm font-semibold">Поступило {newOrdersNotification.count} новых заказов!</strong>
                <div className="text-xs text-emerald-700 mt-0.5">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 rounded-lg p-1 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm"><strong className="font-semibold">Ошибка:</strong> {error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg p-1 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-4">
          {/* Фильтр просмотра для активных заказов */}
          <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Вид:</span>
              <div className="inline-flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewFilter("orders")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewFilter === "orders"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  По заказам
                </button>
                <button
                  onClick={() => setViewFilter("details")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewFilter === "details"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  По деталям
                </button>
                <button
                  onClick={() => setViewFilter("visualizer")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewFilter === "visualizer"
                      ? "bg-white text-purple-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Визуализатор
                </button>
              </div>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-2xl">
              <div className="text-gray-600 text-base font-medium mb-1">
                {searchTerm ? 'Ничего не найдено' : 'Нет заказов'}
              </div>
              {searchTerm && (
                <div className="text-sm text-gray-400">
                  Попробуйте изменить поисковый запрос
                </div>
              )}
            </div>
          ) : viewFilter === "orders" ? (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white p-5 border border-gray-200 shadow-sm rounded-2xl transition-shadow duration-200 hover:shadow-md"
                >
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Тапсырыс №{order.orderNumber}</h2>
                  <p className="text-gray-500 text-sm mt-0.5">{order.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Deal ID: {order.id}</p>
                </div>
              </div>

              {/* Чертежи для производства */}
              {order.productionFiles && order.productionFiles.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Чертежи для производства</h3>
                  <div className="space-y-2">
                    {order.productionFiles.map((file) => (
                      <div key={file.id} className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <span className="text-sm text-gray-700 min-w-0 break-words flex-1">{file.name}</span>
                        <PrintButton fileId={file.id} fileUrl={file.url} label="Печать" color="blue" />
                      </div>
                    ))}
                  </div>
                  <FilesContainer
                    files={order.productionFiles}
                    dealId={order.id}
                  />
                </div>
              )}

              {/* Деталировка к чертежу */}
              {order.files && order.files.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Деталировка к чертежу</h3>
                  <div className="space-y-2">
                    {order.files.map((file) => (
                      <div key={file.id} className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <span className="text-sm text-gray-700 min-w-0 break-words flex-1">{file.name}</span>
                        <PrintButton fileId={file.id} fileUrl={file.url} label="Печать" color="green" />
                      </div>
                    ))}
                  </div>
                  <FilesContainer
                    files={order.files}
                    dealId={order.id}
                  />
                </div>
              )}

              {/* Деталировка стекла */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Деталировка резки
                </h3>
                {renderGlassDetails(order.id, order.orderNumber || order.id)}
              </div>

              {/* Свайп-кнопка во всю ширину блока */}
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={handleStageUpdate}
                  isLoading={processingOrders.has(order.id)}
                />
              </div>
                </div>
              ))}
            </div>
        ) : viewFilter === "visualizer" ? (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-100 p-4 rounded-xl flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-purple-900">Режим визуализатора деталировки</h3>
                <p className="text-sm text-purple-600 mt-0.5">Графическое представление всех деталей на листах стекла, сгруппированных по типам стекла</p>
              </div>
            </div>
            
            {(() => {
              // Собираем все детали из всех заказов
              const allDetails = [];
              const allSelectedInstanceIds = new Set();
              
              filteredOrders.forEach(order => {
                const orderData = ordersDetails[order.id];
                if (orderData && orderData.glassDetails) {
                  orderData.glassDetails.forEach(detail => {
                    allDetails.push({
                      ...detail,
                      orderId: order.id,
                      orderNumber: order.orderNumber || order.id
                    });
                    
                    // Если деталь выбрана, создаем instanceId для всех её экземпляров
                    if (orderData.selectedDetails.has(detail.id)) {
                      const quantity = detail.quantity || 1;
                      for (let i = 0; i < quantity; i++) {
                        allSelectedInstanceIds.add(`${detail.id}_instance_${i}`);
                      }
                    }
                  });
                }
              });

              if (allDetails.length === 0) {
                return (
                  <div className="bg-white p-8 border border-gray-300 rounded-lg text-center">
                    <h3 className="text-lg font-medium text-gray-600 mb-2">Нет деталей для визуализации</h3>
                    <p className="text-sm text-gray-500">
                      Детали появятся после загрузки деталировки заказов
                    </p>
                  </div>
                );
              }

              const handleInstanceClick = (clickedDetail) => {
                if (clickedDetail.isInstanceClick) {
                  const instanceId = clickedDetail.clickedInstanceId;
                  console.log('Клик по экземпляру детали:', {
                    instanceId: instanceId,
                    detailId: clickedDetail.id,
                    orderId: clickedDetail.orderId,
                    instanceNumber: clickedDetail.instanceNumber,
                    totalInstances: clickedDetail.totalInstances
                  });
                  
                  // Переключаем состояние выбора конкретного экземпляра
                  setSelectedInstances(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(instanceId)) {
                      newSet.delete(instanceId);
                      console.log('Убрали выбор экземпляра:', instanceId);
                    } else {
                      newSet.add(instanceId);
                      console.log('Выбрали экземпляр:', instanceId);
                    }
                    return newSet;
                  });
                } else {
                  handleDetailToggle(clickedDetail.orderId, clickedDetail.id, clickedDetail);
                }
              };

              return (
                <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium">Все детали резки</h3>
                    <div className="text-sm text-gray-600 mt-1">
                      Всего деталей: {allDetails.length} из {filteredOrders.length} заказов
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Всего экземпляров: {allDetails.reduce((sum, d) => sum + (d.quantity || 1), 0)} | Выбрано экземпляров: {selectedInstances.size}
                    </div>
                  </div>
                  
                  <GlassVisualizator
                    glassDetails={allDetails}
                    selectedDetails={selectedInstances}
                    onDetailClick={handleInstanceClick}
                  />
                </div>
              );
            })()}
          </div>
        ) : (
          renderDetailsOnly(filteredOrders)
        )}
        </div>
    </div>
  );
};

export default CuttingOrdersPage;